import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowsClockwiseIcon, LinkBreakIcon } from '@phosphor-icons/react';
import { GenericEventFeed } from './GenericEventFeed';
import { EventLinkPickerSubmenu } from './components/EventLinkPickerSubmenu';
import {
    useAddEventToReportMenuItem,
    useOpenInNewWindow,
    useWebSocketStatus,
} from '@/hooks';
import { toast } from '@/components';
import {
    linkEntityToEvent,
    unlinkEntityFromEvent,
} from '@/features/EventView/hooks/useEventEntityLink';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getDetectionLabel, type EntityKind } from '@/utils/detection-classes';
import { isOpenInNewTabClick } from '@/utils/mouse';
import type { DirectoryItemOccurrence } from '@/features/DirectoryItemPage/types';
import { useEventFeedStore } from '@/stores/event-feed.store';
import type { FeedContext } from '@/stores/event-feed.store';

interface DirectoryItemEventFeedProps<T extends DirectoryItemOccurrence> {
    occurrences?: T[] | null;
    isLoading: boolean;
    title: string;
    viewAllUrl: string;
    navigationFrom: string;
    feedContext?: FeedContext;
    playingEventId?: string | null;
    onOccurrenceSelect?: (occurrence: T) => void;
    linkSource?: { kind: EntityKind; id: string };
    onEntityLinked?: () => void;
    onEntityUnlinked?: (eventId: string) => void;
    pagination?: {
        hasMore: boolean;
        isLoadingMore: boolean;
        onLoadMore: () => void;
    };
}

export function DirectoryItemEventFeed<T extends DirectoryItemOccurrence>({
    occurrences,
    isLoading,
    title,
    viewAllUrl,
    navigationFrom,
    feedContext,
    playingEventId,
    onOccurrenceSelect,
    linkSource,
    onEntityLinked,
    onEntityUnlinked,
    pagination,
}: DirectoryItemEventFeedProps<T>) {
    const { t } = useTranslation([
        I18nNamespace.EVENTS,
        I18nNamespace.DIRECTORY,
    ]);
    const location = useLocation();
    const navigate = useNavigate();
    const isConnected = useWebSocketStatus();
    const { openEvent } = useOpenInNewWindow();
    const addToReportItem = useAddEventToReportMenuItem();
    const setPlayerHasSeeked = useEventFeedStore(
        (state) => state.setPlayerHasSeeked,
    );

    const handleViewAll = useCallback(() => {
        navigate(viewAllUrl);
    }, [navigate, viewAllUrl]);

    const handleRelink = useCallback(
        async (eventId: string, kind: EntityKind, entityId: string) => {
            try {
                await linkEntityToEvent(eventId, kind, entityId);
                toast.success(t('events:eventView.linkEntity.success'));
                onEntityLinked?.();
            } catch {
                toast.error(t('events:eventView.linkEntity.errorLink'));
            }
        },
        [onEntityLinked, t],
    );

    const handleUnlink = useCallback(
        async (eventId: string) => {
            if (!linkSource) return;
            const { kind, id } = linkSource;
            try {
                await unlinkEntityFromEvent(eventId, kind);
                if (onEntityUnlinked) {
                    onEntityUnlinked(eventId);
                } else {
                    onEntityLinked?.();
                }
                toast.success(t('events:eventView.linkEntity.successUnlink'), {
                    duration: 8000,
                    action: {
                        label: t('events:eventView.linkEntity.undo'),
                        onClick: () => void handleRelink(eventId, kind, id),
                    },
                });
            } catch {
                toast.error(t('events:eventView.linkEntity.errorUnlink'));
            }
        },
        [linkSource, onEntityLinked, onEntityUnlinked, handleRelink, t],
    );

    const items = occurrences ?? [];
    const hasData = items.length > 0;

    return (
        <GenericEventFeed
            containerTitle={title}
            isLive={false}
            isConnected={isConnected}
            publishesLayoutVisibility={false}
            hasData={hasData}
            onViewAll={handleViewAll}
            items={items}
            isLoading={isLoading}
            itemKey={(occurrence) => occurrence.event.id}
            itemEvent={(occurrence) => occurrence.event}
            cardTitle={t('events:page.event')}
            itemDescription={(occurrence) =>
                getDetectionLabel(t, occurrence.event.classId)
            }
            itemConfidence={(occurrence) =>
                occurrence.confidence != null
                    ? occurrence.confidence * 100
                    : undefined
            }
            itemActionMenuItems={(occurrence, event) => [
                ...addToReportItem(event, {
                    confidence:
                        occurrence.confidence != null
                            ? occurrence.confidence * 100
                            : undefined,
                }),
                ...(linkSource
                    ? [
                          {
                              key: 'link',
                              label: t(
                                  'events:eventView.entityMenu.replaceLinkedEntity',
                              ),
                              icon: ArrowsClockwiseIcon,
                              submenu: (
                                  <EventLinkPickerSubmenu
                                      entityKind={linkSource.kind}
                                      sourceEntityId={linkSource.id}
                                      eventId={occurrence.event.id}
                                      onLinked={onEntityLinked}
                                  />
                              ),
                          },
                          {
                              key: 'unlink',
                              label: t(
                                  'events:eventView.entityMenu.unlinkFromEvent',
                              ),
                              icon: LinkBreakIcon,
                              onSelect: () =>
                                  void handleUnlink(occurrence.event.id),
                          },
                      ]
                    : []),
            ]}
            watchingOptions={(_, event) => ({
                isFocused: playingEventId === event.id,
            })}
            onClick={(occurrence, clickEvent) => {
                if (onOccurrenceSelect && !isOpenInNewTabClick(clickEvent)) {
                    clickEvent.preventDefault();
                    clickEvent.stopPropagation();
                    setPlayerHasSeeked(false);
                    onOccurrenceSelect(occurrence);
                    return;
                }
                openEvent(occurrence.event.id, clickEvent, {
                    state: {
                        from: navigationFrom,
                        returnUrl: location.pathname + location.search,
                        feedContext,
                    },
                });
            }}
            quickViewContextType="event-feed"
            pagination={pagination}
        />
    );
}
